import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, FormControlLabel, MenuItem, Radio, RadioGroup, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditorResultText from './Shared/RichEditorResultText';
import RichEditor from './Shared/RichEditor';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import {useGetApplicationStatusesQuery} from '../../../../../api/services/schoolManagementService';
import FORM_AUDIENCE_TYPE from '../../../../../constants/FormAudienceType';
import {PARENT_AUDIENCE_TYPE} from '../../../../../constants/ParentAudienceType';

const OfferStatusChoice = ({
                               index,
                               componentId,
                               value,
                               onChange,
                               descriptionHtml,
                               onDescriptionHtmlChange,
                               commitDescriptionHtml,
                               isEditMode,
                               enableValidation,
                               options,
                               isFormArchived,
                               relatedComponent,
                               readOnly,
                               placeholderValues,
                               accessLevel,
                               audience,
                               parentAudienceType,
                               recipientType,
                               linkedToStudents,
                               formPreviewMode,
                               submitMode
                           }) => {
    const {data: applicationStatuses = []} = useGetApplicationStatusesQuery({}, {skip: !isEditMode});
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDescriptionChange = (nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        if (onDescriptionHtmlChange) onDescriptionHtmlChange(nextHtml);
    };

    const handleDescriptionBlur = () => {
        setIsEditorFocused(false);
        if (commitDescriptionHtml) commitDescriptionHtml();
    };

    const safeOptions = options ?? [];
    const shouldShowAudienceWarning =
        !submitMode &&
        !formPreviewMode &&
        !(
            Number(audience) === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            Number(parentAudienceType) === PARENT_AUDIENCE_TYPE.applicant
        );

    return (
        <Box sx={{mt: 2}}>
            {shouldShowAudienceWarning && (
                <Alert severity="warning" sx={{mb: 2}}>
                    This component only updates application status for applicants. It will not update
                    enquiry or student records when used with those audiences.
                </Alert>
            )}
            {isEditMode
                ? (
                    <>
                        <Box>
                            <RichEditor
                                currentDescription={draftDescriptionHtml}
                                setCurrentDescription={handleDescriptionChange}
                                isFormLive={isFormArchived}
                                accessLevel={accessLevel}
                                audience={audience}
                                recipientType={recipientType}
                                linkedToStudents={linkedToStudents}
                                placeholderValues={placeholderValues}
                                onFocus={() => setIsEditorFocused(true)}
                                onBlur={handleDescriptionBlur}
                            />

                            <Box>
                                {safeOptions.map((option, optionIndex) => (
                                    <Box
                                        key={`offerStatusChoiceOption-${option.id ?? optionIndex}`}
                                        py={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        gap={1}
                                        alignItems="center"
                                    >
                                        <Typography>{optionIndex + 1}.</Typography>
                                        <TextField
                                            multiline
                                            fullWidth
                                            sx={{flex: 2}}
                                            value={option.description ?? ''}
                                            disabled={isFormArchived}
                                            label={!option.description ? 'Enter a choice for your respondents to select' : null}
                                            InputLabelProps={{shrink: false}}
                                            onChange={(e) =>
                                                onChange({
                                                    index,
                                                    option: {
                                                        ...option,
                                                        description: e.target.value,
                                                    },
                                                    optionId: option.id
                                                })
                                            }
                                        />
                                        <TextField
                                            select
                                            required
                                            sx={{flex: 1}}
                                            error={
                                                !option?.value ||
                                                (option?.value &&
                                                    !applicationStatuses.some(
                                                        x => `${x.id}` === option.value
                                                    ))
                                            }
                                            value={option.value}
                                            label="Application Status"
                                            onChange={e =>
                                                onChange({
                                                    index,
                                                    option: {
                                                        ...option,
                                                        value: e.target.value,
                                                    },
                                                    optionId: option.id,
                                                    isValid:
                                                        !option?.value ||
                                                        (option?.value &&
                                                            !applicationStatuses.some(
                                                                x => `${x.id}` === option.value
                                                            )),
                                                })
                                            }
                                        >
                                            {applicationStatuses.map(({
                                                                          id: statusId,
                                                                          description: statusDescription
                                                                      }) => (
                                                <MenuItem
                                                    key={statusId}
                                                    value={`${statusId}`}
                                                >
                                                    {statusDescription}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <Tooltip
                                            title={
                                                relatedComponent?.targetState === option.id
                                                    ? `This option cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                                                    : null
                                            }
                                            disabled={isFormArchived}
                                        >
                                            <IconButton>
                                                <DeleteIcon
                                                    onClick={() =>
                                                        relatedComponent?.targetState === option.id || isFormArchived
                                                            ? {}
                                                            : onChange({
                                                                index,
                                                                isRemove: true,
                                                                optionId: option.id,
                                                            })
                                                    }
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </Box>
                            <Button
                                onClick={() => onChange({
                                    index,
                                    isAdd: true
                                })}
                                startIcon={<AddIcon/>}
                                cursor="pointer"
                                disabled={isFormArchived}
                            >
                                Add option
                            </Button>
                        </Box>
                        <RequiredCheckbox
                            enableValidation={enableValidation}
                            index={index}
                            onChange={onChange}
                            isFormArchived={isFormArchived}
                        />
                    </>
                )
                : (
                    <Box key={`offerStatusChoice-${index}`}>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        <RadioGroup
                            value={value || ''}
                            onClick={e =>
                                onChange({
                                    id: componentId,
                                    radioValue: e.target.value === value ? null : e.target.value,
                                })
                            }
                        >
                            {options.map((option, optionIndex) => (
                                <FormControlLabel
                                    key={`offerStatusChoiceOption-${option.id ?? optionIndex}`}
                                    sx={{
                                        '& .MuiTypography-root': {
                                            wordBreak: 'break-word',
                                            maxWidth: '800px',
                                        },
                                    }}
                                    value={option.id}
                                    control={<Radio/>}
                                    label={option.description}
                                />
                            ))}
                        </RadioGroup>
                    </Box>
                )}
        </Box>
    );
};

OfferStatusChoice.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    index: PropTypes.number,
    options: PropTypes.array,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    formPreviewMode: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default OfferStatusChoice;
